// deployment_class: customer_env_allowed
/**
 * Better Auth browser client — Task 11a (frontend password login, MINIMAL).
 *
 * This is the SOLE transport for credential operations (sign up / sign in /
 * sign out). It talks to the Better Auth route surface mounted by the V3
 * server at `/api/auth/*` (see `src/v3/server/server.ts` —
 * `fastify.all('/api/auth/*', …)`). Better Auth is the SOLE authenticator on
 * `main` (the legacy JWT `alg:none` stub is deleted server-side).
 *
 * SCOPE (Task 11a): password-only baseline. MFA / passkey are OPT-IN and are
 * deferred to Task 11b — NO WebAuthn / TOTP client wiring here.
 *
 * SESSION STATE: the session is carried by an httpOnly cookie set by Better
 * Auth (`kth`-prefixed, SameSite=Strict). The browser CANNOT read it. Our app
 * derives the authenticated actor from the V3 `/v3/auth/me` endpoint (see
 * `useAuth.ts`), NOT from the cookie. We therefore use the VANILLA client
 * (`better-auth/client`) rather than the React client — we drive our own
 * Zustand store off `/v3/auth/me`, so we don't need Better Auth's React
 * `useSession` hook, and the vanilla client avoids a cross-package React
 * resolution concern in this monorepo (react lives in web/v3/node_modules;
 * better-auth is hoisted to the repo-root node_modules).
 *
 * baseURL: defaults to same-origin `<origin>/api/auth` so the Vite dev proxy
 * (and the production same-origin deploy) route it to the V3 server. Better
 * Auth's client requires an ABSOLUTE URL (it parses `new URL(baseURL)`), so we
 * derive the origin from `window.location` rather than passing a bare path.
 * Overridable via `VITE_BETTER_AUTH_URL` for split-origin local setups.
 */
import { createAuthClient } from 'better-auth/client';

export function resolveBaseUrl(): string {
  const override = import.meta.env?.VITE_BETTER_AUTH_URL as string | undefined;
  if (override) return override;
  // Same-origin default. `window.location.origin` is present in the browser and
  // in jsdom (test env); fall back to a localhost dev origin if somehow absent.
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost:5173';
  return `${origin}/api/auth`;
}

export const authClient = createAuthClient({
  baseURL: resolveBaseUrl(),
});

/**
 * Normalizes a Better Auth client error/response into a human-readable
 * message. Better Auth client methods resolve to `{ data, error }` rather than
 * throwing; the caller checks `error` and passes it here.
 */
export function authErrorMessage(
  error: { message?: string; statusText?: string; status?: number } | null,
  fallback: string,
): string {
  if (!error) return fallback;
  return error.message || error.statusText || fallback;
}

/**
 * Sign out of the CURRENT origin's session.
 *
 * Deliberately a raw same-origin **relative-path** credentialed POST rather
 * than `authClient.signOut()`. On a multi-domain deployment (app.kathedral.ai,
 * everett.kathedral.ai, …) the session cookie is **host-scoped**
 * (`__Secure-kth.session_token`, no `Domain` attribute — see
 * better-auth-config `crossSubDomainCookies.enabled:false`). A relative path
 * can NEVER resolve to a different origin, so the request always targets the
 * host the user is actually on and carries that host's cookie — guaranteeing
 * the server deletes the right session and its clear-cookie response applies.
 * (`credentials: 'include'` is explicit belt-and-suspenders.)
 *
 * NO `Content-Type` header: this is a NO-BODY POST. Declaring
 * `application/json` with an empty body makes a JSON-parsing server reject the
 * request — Fastify's default parser throws "Body cannot be empty when
 * content-type is set to 'application/json'", which surfaced as a HTTP 500 and
 * left the session un-revoked (the production logout bug). Omitting the header
 * tells the server there is no body to parse. (The server also now hardens
 * against this class via an empty-body-tolerant JSON parser — see
 * `src/v3/server/server.ts` `addContentTypeParser` — but the client must not
 * lie about a body it does not send.)
 */
export async function serverSignOut(): Promise<void> {
  await fetch('/api/auth/sign-out', {
    method: 'POST',
    credentials: 'include',
  });
}
